<template>
  <Modal class="tos-modal" @close="closeTos" v-if="isTermsModalOpened">
    <template #headline>利用規約への同意のご確認</template>
    <template #body>
      <p class="tos-description">このサービスをご利用になる場合、利用規約への同意が必要となります。利用規約をご確認の上、ご利用を開始してください。</p>
      <a class="tos-link" :href="termsUrl" target="_blank" @click.once="tosLinkClick">
        <p class="tos-link-text">
          {{ eventName }}<br />
          利用規約
        </p>
      </a>
      <label class="check tos-check">
        <input class="check-input" :disabled="(isWebView || useLinkageAppAndNecFlag) && !tosLink" type="checkbox" v-model="tosCheck" />
        <span class="check-label">
          <span class="check-label-text">利用規約に同意する</span>
        </span>
      </label>
    </template>
    <template #footer>
      <ul class="btnRow">
        <li class="btnRow-item">
          <ActionButton
            class="btn btn-rd btn-lg btn-red"
            type="button"
            :handle-submit="acceptPolicy"
            :disabled="!startDisabled || isBtnDisabled"
            button-text="利用開始する"
          />
        </li>
        <li class="btnRow-item">
          <button class="btn btn-rd btn-sm btn-bd-gray" type="button" @click="closeTos">キャンセル</button>
        </li>
      </ul>
    </template>
  </Modal>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { isChecked } from '@/helpers/validators';
import Modal from '@/components/Modal.vue';
import { useError } from '@/mixins/error';
import { useVuelidate } from '@vuelidate/core';

const store = useStore();
const router = useRouter();
const route = useRoute();

const tosLink = ref(false);
const tosCheck = ref(false);
const isBtnDisabled = ref(false);

const rules = computed(() => ({
  tosLink: { tosCheck: isChecked },
  tosCheck: { tosCheck: isChecked },
}));

const v$ = useVuelidate(rules, { tosLink, tosCheck });

const childEventInfo = computed(() => store.getters['event/childEventInfo']);
const isTermsModalOpened = computed(() => store.getters['common/isTermsModalOpened']);
const isWebView = computed(() => store.getters['common/isWebView']);
const hasPasscode = computed(() => store.getters['auth/hasPasscode']);
const isVerify = computed(() => store.getters['auth/isVerify']);
const isLoggedIn = computed(() => store.getters['auth/token']);
const identityVerification = computed(() => store.getters['event/identityVerification']);
const requestTypeMomentIdentity = computed(() => store.getters['event/requestTypeMomentIdentity']);
const infor = computed(() => store.getters['auth/infor']);
const useLinkageAppAndNecFlag = computed(() => store.getters['event/useLinkageAppAndNecFlag']);
const maximumAmount = computed(() => store.getters['auth/maximumAmount']);
const isMatchKeyWord = computed(() => store.getters['event/isMatchKeyWord']);
const startDisabled = computed(() => tosCheck.value && tosLink.value);
const termsUrl = computed(() => childEventInfo.value?.termsUrl || '');
const eventName = computed(() => childEventInfo.value?.name || '');
const useAuthentication = computed(() => store.getters['event/childEventInfo']?.useAuthentication || null);
const directory = computed(() => route.params?.directory || route.query.directory || localStorage.getItem('directory'));
const registId = computed(() => sessionStorage.getItem('registId'));

const { getErrors } = useError();

const closeTos = () => {
  store.dispatch('common/openTermsModal', false);
  localStorage.removeItem('directory');
  localStorage.removeItem('isTosLinkOpened');
  localStorage.removeItem('tosCheck');
};
const acceptPolicy = async () => {
  isBtnDisabled.value = true;
  v$.value.$touch();
  if (v$.value.$invalid) {
    getErrors(v$.value);
    isBtnDisabled.value = false;
  } else {
    await store.dispatch('event/getChildEventInfo', { directory: directory.value });
    await checkAuthentication('checkAuthenticationToken');
    const isEventVerified = !!isLoggedIn.value && identityVerification.value && requestTypeMomentIdentity.value;
    if (isEventVerified && !isVerify.value) {
      if (route.name === 'PincodeDefault' && route.query?.directory) {
        window.location.href = window.location.origin + '/#/' + `${route.query?.directory}/identity/confirm`;
      } else {
        window.location.href = window.location.origin + '/#/' + `${route.params?.directory || route.query?.directory}/identity/confirm`;
      }
      closeTos();
      isBtnDisabled.value = false;
      return;
    } else if (isEventVerified && isVerify.value && !isMatchKeyWord.value) {
      if (route.name === 'PincodeDefault' && route.query?.directory) {
        window.location.href = window.location.origin + '/#/' + `${route.query?.directory}/identity/finish?hasMatchKeyword=true`;
      } else {
        window.location.href = window.location.origin + '/#/' + `${route.params?.directory || route.query?.directory}/identity/finish?hasMatchKeyword=true`;
      }
      closeTos();
      isBtnDisabled.value = false;
      return;
    }
    const result = await store.dispatch('auth/acceptPolicy', {
      directory: childEventInfo?.value?.directory,
      registId: registId.value,
      purchaseAmountLimit: maximumAmount.value,
    });
    if (result) {
      await checkAuthentication('invalidAuthenticationData');
      checkPincode();
      closeTos();
      isBtnDisabled.value = false;
      sessionStorage.removeItem('registId');
    } else {
      isBtnDisabled.value = false;
    }
  }
};
const checkAuthentication = async (action) => {
  if (useAuthentication.value) {
    await store.dispatch(`auth/${action}`, {
      useAuthentication: useAuthentication.value,
    });
  }
};
const checkPincode = () => {
  const query = hasPasscode.value ? null : { directory: route.params.directory };
  const routeName = hasPasscode.value ? 'Home' : 'PincodeDefault';
  if (useAuthentication.value) {
    router.push({ name: routeName, query });
  } else if (route.name !== 'PincodeDefault' && infor.value?.usePaymentPasscode && !hasPasscode.value) {
    router.push({ name: 'PincodeDefault', query });
  } else if (route.name === 'Authentication' && !useAuthentication.value) {
    router.push({ name: '404' });
  }
};
const tosLinkClick = () => {
  tosLink.value = true;
  if (isWebView.value || useLinkageAppAndNecFlag.value) {
    localStorage.setItem('isTosLinkOpened', true);
    localStorage.setItem('tosCheck', tosCheck.value);
    localStorage.setItem('directory', childEventInfo?.value?.directory);
  }
};

watch(isTermsModalOpened, (isOpened) => {
  if (!isOpened) {
    tosLink.value = false;
    tosCheck.value = false;
  }
});

onMounted(async() => {
  const isTosLinkOpened = JSON.parse(localStorage.getItem('isTosLinkOpened'));
  const directory = localStorage.getItem('directory');
  tosCheck.value = JSON.parse(localStorage.getItem('tosCheck'));
  if ((isWebView.value || useLinkageAppAndNecFlag.value) && isTosLinkOpened) {
    store.dispatch('event/getChildEventInfo', { directory });
    store.dispatch('common/openTermsModal', true);
    tosLink.value = localStorage.getItem('isTosLinkOpened');
  }
  if (route.name === 'PincodeDefault' && route.query.directory) {
    await store.dispatch('event/getChildEventInfo', { directory: route.query.directory });
  }
});

onBeforeUnmount(() => {
  sessionStorage.removeItem('registId');
});
</script>

<style scoped>
.check-input:disabled + .check-label {
  color: #b0b0b0;
}
</style>
